"use client";

import Link from "next/link";
import Image from "next/image";
import { useState, useEffect } from "react";
import { usePathname } from "next/navigation";
import {
  FlaskConical,
  Home,
  Gift,
  Info,
  LogIn,
  LogOut,
  Mail,
  Menu,
  Sparkles,
  UserPlus,
  X,
} from "lucide-react";
import { me, logoutAction } from "@/lib/actions/auth";

// "Today" moved to the footer's reference column 2026-09-08 — it is a returning
// visitor's feed rather than a primary entry point.
// An explicit Home link was added the same day: relying on the logo assumes
// people know it is clickable, and plenty do not.
const NAV = [
  { label: "Home", href: "/", Icon: Home },
  { label: "Game", href: "/games", Icon: Sparkles },
  { label: "Reward", href: "/rewards", Icon: Gift },
  { label: "Research", href: "/research", Icon: FlaskConical },
  { label: "About", href: "/about", Icon: Info },
  { label: "Contact", href: "/contact", Icon: Mail },
];

// Home is "/" and startsWith("/") is true for every route, so it needs an exact
// match; the rest stay prefix matches so /games/... keeps Game highlighted.
function isActive(pathname: string | null, href: string): boolean {
  if (!pathname) return false;
  return href === "/" ? pathname === "/" : pathname.startsWith(href);
}

export default function Header() {
  const [open, setOpen] = useState(false);
  const pathname = usePathname();
  const [user, setUser] = useState<{ id: string; name: string } | null>(null);

  useEffect(() => {
    let active = true;
    me().then((u) => { if (active) setUser(u); });
    return () => { active = false; };
  }, [pathname]);

  const firstName = user?.name.trim().split(/\s+/)[0] ?? "";

  return (
    <header
      className="sticky top-0 z-30 bg-white/95 backdrop-blur"
      style={{ borderBottom: "1.5px solid #dde1e7" }}
    >
      <div className="mx-auto flex max-w-7xl items-center gap-3 px-4 py-3 md:px-6">

        {/* Logo */}
        <Link href="/" className="flex min-w-0 shrink items-center gap-1.5" aria-label="Morning Reward home">
          <Image
            src="/header-logo.png"
            alt="Morning Reward"
            height={96}
            width={96}
            quality={90}
            className="h-9 w-9 shrink-0 object-contain sm:h-11 sm:w-11"
            priority
          />
          <span
            className="truncate text-lg font-black tracking-tight sm:text-xl lg:text-2xl"
            style={{ fontFamily: "var(--font-nunito)" }}
          >
            <span style={{ color: "#1877F2" }}>MorningReward</span>
            <span style={{ color: "#1877F2", fontSize: "0.6em", fontWeight: 700, opacity: 0.55, letterSpacing: 0 }}>.com</span>
          </span>
        </Link>

        {/* Nav links — desktop */}
        <nav className="ml-auto hidden items-center gap-0.5 lg:flex">
          {NAV.map((n) => {
            const active = isActive(pathname, n.href);
            return (
              <Link
                key={n.href}
                href={n.href}
                aria-current={active ? "page" : undefined}
                className="rounded-full px-3.5 py-2 text-sm font-semibold transition"
                style={
                  active
                    ? { background: "#eff6ff", color: "#1877F2" }
                    : { color: "#6b7280" }
                }
              >
                {n.label}
              </Link>
            );
          })}
        </nav>

        {/* Auth — desktop */}
        <div className="ml-3 hidden items-center gap-2 lg:flex">
          {user ? (
            <>
              <span className="px-2 text-sm font-bold text-[#374151]">Hi, {firstName}</span>
              <form action={logoutAction}>
                <button
                  type="submit"
                  className="flex items-center gap-1.5 rounded-full border px-4 py-2 text-sm font-bold text-[#374151] transition hover:border-[#1877F2] hover:text-[#1877F2]"
                  style={{ border: "1.5px solid #dde1e7" }}
                >
                  <LogOut className="h-4 w-4" aria-hidden />
                  Sign out
                </button>
              </form>
            </>
          ) : (
            <>
              <Link
                href="/login"
                className="rounded-full border px-4 py-2 text-sm font-bold text-[#374151] transition hover:border-[#1877F2] hover:text-[#1877F2]"
                style={{ border: "1.5px solid #dde1e7" }}
              >
                Sign in
              </Link>
              <Link
                href="/signup"
                className="rounded-full px-4 py-2 text-sm font-bold text-white shadow-sm transition hover:opacity-90"
                style={{ background: "#1877F2" }}
              >
                Sign up
              </Link>
            </>
          )}
        </div>

        {/* Mobile: burger */}
        <div className="ml-auto flex items-center gap-2 lg:hidden">
          <button
            type="button"
            aria-label={open ? "Close menu" : "Open menu"}
            aria-expanded={open}
            onClick={() => setOpen((v) => !v)}
            className="grid h-10 w-10 place-items-center rounded-xl text-[#374151] transition hover:bg-[#f1f5f9]"
            style={{ border: "1.5px solid #dde1e7" }}
          >
            {open ? <X className="h-5 w-5" aria-hidden /> : <Menu className="h-5 w-5" aria-hidden />}
          </button>
        </div>
      </div>

      {/* Mobile menu */}
      {open && (
        <div
          className="lg:hidden"
          style={{ borderTop: "1.5px solid #dde1e7", background: "#f8fafc" }}
        >
          <div className="px-4 pt-4 pb-6 space-y-4">

            {/* Nav grid */}
            <div className="grid grid-cols-3 gap-3">
              {NAV.map((n) => {
                const active = isActive(pathname, n.href);
                const Icon = n.Icon;
                return (
                  <Link
                    key={n.href}
                    href={n.href}
                    onClick={() => setOpen(false)}
                    aria-current={active ? "page" : undefined}
                    className="flex flex-col items-center gap-2 rounded-2xl py-4 text-sm font-bold transition active:opacity-75"
                    style={{
                      background: active ? "#1877F2" : "white",
                      color: active ? "white" : "#374151",
                      border: "1.5px solid",
                      borderColor: active ? "#1877F2" : "#dde1e7",
                    }}
                  >
                    <Icon className="h-6 w-6" aria-hidden />
                    {n.label}
                  </Link>
                );
              })}
            </div>

            {/* Auth */}
            {user ? (
              <div className="pt-1">
                <p className="mb-2 px-1 text-sm font-bold text-[#374151]">Signed in as {firstName}</p>
                <form action={logoutAction}>
                  <button
                    type="submit"
                    className="flex w-full items-center justify-center gap-2 rounded-2xl py-3.5 text-sm font-bold text-[#374151]"
                    style={{ background: "white", border: "1.5px solid #dde1e7" }}
                  >
                    <LogOut className="h-4 w-4 text-[#1877F2]" aria-hidden />
                    Sign out
                  </button>
                </form>
              </div>
            ) : (
              <div className="grid grid-cols-2 gap-3 pt-1">
                <Link
                  href="/login"
                  onClick={() => setOpen(false)}
                  className="flex items-center justify-center gap-2 rounded-2xl py-3.5 text-sm font-bold text-[#374151]"
                  style={{ background: "white", border: "1.5px solid #dde1e7" }}
                >
                  <LogIn className="h-4 w-4 text-[#1877F2]" aria-hidden />
                  Sign in
                </Link>
                <Link
                  href="/signup"
                  onClick={() => setOpen(false)}
                  className="flex items-center justify-center gap-2 rounded-2xl py-3.5 text-sm font-bold text-white"
                  style={{ background: "#1877F2" }}
                >
                  <UserPlus className="h-4 w-4" aria-hidden />
                  Sign up
                </Link>
              </div>
            )}

          </div>
        </div>
      )}
    </header>
  );
}
